import { useEffect, useMemo, useState } from 'react';
import { RefreshCw, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { driverService } from '../services/driverService';
import { seyirViolationService } from '../services/seyirViolationService';
import * as XLSX from 'xlsx-js-style';

type ComparisonRow = {
  driverId: number;
  userId: number;
  driverName: string | null;
  phone: string | null;
  vehicleId: number | null;
  systemVehiclePlate: string | null;
  seyirVehiclePlate: string | null;
  systemTrailerPlate: string | null;
  seyirTrailerPlate: string | null;
  systemDriverName: string | null;
  seyirDriverName: string | null;
  systemDriverLicenseNo: string | null;
  seyirDigitalLicenseNo: string | null;
  licenseMatchKnown: boolean;
  licenseMatches: boolean;
  hasLiveSeyirCard: boolean;
  locationUpdatedAt: string | null;
  currentAddress: string | null;
  ownerType: string;
  currentSpeed: number;
  isVehicleRunning: boolean;
  activeShipmentCount: number;
  plateMatches: boolean;
  driverMatches: boolean;
  hasMismatch: boolean;
};

type ActiveWorkiomShipmentRow = {
  id: number;
  shipmentNo: string;
  orderCode: string;
  workiomRecordId?: string | null;
  transportModel: string;
  carrierType: string;
  workiomStatusLabel: string;
  processStatus: string;
  vehiclePlate: string;
  trailerPlate: string;
  driverName: string;
  pickup: string;
  delivery: string;
  latitude?: number | null;
  longitude?: number | null;
  currentAddress: string;
  locationUpdatedAt?: string | null;
};

type SeyirViolationRow = {
  id: number;
  violation_type: string;
  severity: string;
  first_detected_at: string;
  last_detected_at: string;
  check_count: number;
  tractor_plate: string | null;
  trailer_plate: string | null;
  system_driver_name: string | null;
  system_driver_license_no: string | null;
  seyir_driver_name: string | null;
  seyir_digital_license_no: string | null;
  matched_driver_name?: string | null;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('tr-TR');
};

export function SeyirDriverComparison() {
  const [rows, setRows] = useState<ComparisonRow[]>([]);
  const [missingRows, setMissingRows] = useState<ComparisonRow[]>([]);
  const [movingRows, setMovingRows] = useState<ComparisonRow[]>([]);
  const [activeWorkiomShipments, setActiveWorkiomShipments] = useState<ActiveWorkiomShipmentRow[]>([]);
  const [violations, setViolations] = useState<SeyirViolationRow[]>([]);
  const [activeWorkiomSearch, setActiveWorkiomSearch] = useState('');
  const [violationStartDate, setViolationStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [violationEndDate, setViolationEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [activeViolationTab, setActiveViolationTab] = useState<'all' | string>('all');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [scanningViolations, setScanningViolations] = useState(false);

  const loadRows = async () => {
    try {
      setLoading(true);
      const response: any = await driverService.getSeyirDriverComparisons();
      const data = response?.data || {};
      setRows(Array.isArray(data.mismatches) ? data.mismatches : []);
      setMissingRows(Array.isArray(data.missingMobileData) ? data.missingMobileData : []);
      setMovingRows(Array.isArray(data.movingWithoutActiveJobs) ? data.movingWithoutActiveJobs : []);
      setActiveWorkiomShipments(Array.isArray(data.activeWorkiomShipments) ? data.activeWorkiomShipments : []);
      await loadViolations();
    } catch (error: any) {
      console.error('Seyir comparison load failed:', error);
      toast.error(error?.message || 'Seyir karşılaştırma verileri alınamadı');
    } finally {
      setLoading(false);
    }
  };

  const loadViolations = async () => {
    const response: any = await seyirViolationService.getViolations({ startDate: violationStartDate, endDate: violationEndDate, limit: 500 });
    setViolations(Array.isArray(response?.data) ? response.data : []);
  };

  useEffect(() => {
    void loadRows();
  }, []);

  const handleSync = async () => {
    try {
      setSyncing(true);
      const response: any = await driverService.syncInternalDriversFromSeyir();
      toast.success(response?.message || 'Seyir verileri güncellendi');
      await loadRows();
    } catch (error: any) {
      console.error('Seyir sync failed:', error);
      toast.error(error?.message || 'Seyir verileri güncellenemedi');
    } finally {
      setSyncing(false);
    }
  };

  const handleViolationScan = async () => {
    try {
      setScanningViolations(true);
      const response: any = await seyirViolationService.scanNow();
      const result = response?.data || {};
      const summaryParts = [`Taranan: ${result.scanned || 0}`, `loglanan: ${result.logged || 0}`, `iyileştirilen: ${result.healed || 0}`];
      if (result.error) {
        toast.error(`Kontrol kısmen tamamlandı. ${summaryParts.join(', ')}. Hata: ${result.error}`);
      } else {
        toast.success(`Kontrol tamamlandı. ${summaryParts.join(', ')}`);
      }
      await loadViolations();
    } catch (error: any) {
      console.error('Seyir violation scan failed:', error);
      toast.error(error?.message || 'Dijital ehliyet kontrolü çalıştırılamadı');
    } finally {
      setScanningViolations(false);
    }
  };

  const summary = useMemo(() => ({
    total: rows.length,
    plateMismatch: rows.filter((row) => !row.plateMatches).length,
    driverMismatch: rows.filter((row) => !row.driverMatches).length,
    missingMobileData: missingRows.length,
    movingWithoutActiveJobs: movingRows.length,
  }), [rows, missingRows, movingRows]);

  const violationTabs = useMemo(() => {
    const counts = violations.reduce<Record<string, number>>((acc, row) => {
      const key = String(row.violation_type || 'unknown');
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    return [
      { key: 'all', label: 'Tümü', count: violations.length },
      ...Object.entries(counts).map(([key, count]) => ({
        key,
        label: key === 'unknown_digital_license' ? 'Bilinmeyen Ehliyet' : 'Atama Farkı',
        count,
      })),
    ];
  }, [violations]);

  const filteredViolations = useMemo(() => {
    if (activeViolationTab === 'all') return violations;
    return violations.filter((row) => String(row.violation_type || '') === activeViolationTab);
  }, [violations, activeViolationTab]);

  const filteredActiveWorkiomShipments = useMemo(() => {
    const query = activeWorkiomSearch.trim().toLocaleLowerCase('tr-TR');
    if (!query) return activeWorkiomShipments;
    return activeWorkiomShipments.filter((row) =>
      [row.vehiclePlate, row.driverName, row.shipmentNo, row.orderCode, row.pickup, row.delivery, row.transportModel, row.carrierType, row.workiomStatusLabel, row.processStatus]
        .some((value) => String(value || '').toLocaleLowerCase('tr-TR').includes(query))
    );
  }, [activeWorkiomShipments, activeWorkiomSearch]);

  const handleExportExcel = () => {
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(missingRows.map((row) => ({
      'Sürücü': row.driverName || '-',
      'Telefon': row.phone || '-',
      'Sistem Çekici': row.systemVehiclePlate || '-',
      'Sistem Dorse': row.systemTrailerPlate || '-',
      'Son Konum Zamanı': formatDateTime(row.locationUpdatedAt),
      'Durum': 'Seyir veri yok',
    }))), 'SeyirVeriYok');
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(movingRows.map((row) => ({
      'Sürücü': row.driverName || '-',
      'Sistem Çekici': row.systemVehiclePlate || '-',
      'Sistem Dorse': row.systemTrailerPlate || '-',
      'Hız': row.currentSpeed ? `${row.currentSpeed.toLocaleString('tr-TR')} km/s` : row.isVehicleRunning ? 'Hareket var' : '-',
      'Son Konum Zamanı': formatDateTime(row.locationUpdatedAt),
      'Mevcut Konum': row.currentAddress || '-',
    }))), 'HareketliIsYok');
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows.map((row) => ({
      'Araç ID': row.vehicleId || '-',
      'Sistem Çekici': row.systemVehiclePlate || '-',
      'Seyir Çekici': row.seyirVehiclePlate || '-',
      'Sistem Sürücü': row.systemDriverName || '-',
      'Seyir Sürücü': row.seyirDriverName || '-',
      'Sistem Ehliyet No': row.systemDriverLicenseNo || '-',
      'Seyir Dijital Ehliyet No': row.seyirDigitalLicenseNo || '-',
      'Sistem Dorse': row.systemTrailerPlate || '-',
      'Seyir Dorse': row.seyirTrailerPlate || '-',
      'Konum Zamanı': formatDateTime(row.locationUpdatedAt),
      'Mevcut Konum': row.currentAddress || '-',
    }))), 'UyumsuzKayitlar');
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(activeWorkiomShipments.map((row) => ({
      'Sevkiyat No': row.shipmentNo,
      'Sipariş No': row.orderCode,
      'Taşıma Modeli': row.transportModel,
      'Nakliyeci Tipi': row.carrierType,
      'Durum': row.workiomStatusLabel,
      'İşlem Durumu': row.processStatus,
      'Plaka': row.vehiclePlate,
      'Dorse': row.trailerPlate,
      'Sürücü': row.driverName,
      'Yükleme': row.pickup,
      'Teslim': row.delivery,
      'Konum Zamanı': formatDateTime(row.locationUpdatedAt),
      'Sürücü Konumu': row.currentAddress,
    }))), 'AktifWorkiomSevkiyat');
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(violations.map((row) => ({
      'İlk Tespit': formatDateTime(row.first_detected_at),
      'Son Tespit': formatDateTime(row.last_detected_at),
      'Tekrar': row.check_count,
      'İhlal Tipi': row.violation_type === 'unknown_digital_license' ? 'Bilinmeyen dijital ehliyet' : 'Atanan sürücü farklı',
      'Çekici': row.tractor_plate || '-',
      'Dorse': row.trailer_plate || '-',
      'Araç Sürücüsü': row.system_driver_name || '-',
      'Araç Sürücüsü Ehliyet No': row.system_driver_license_no || '-',
      'Seyir Dijital Ehliyet No': row.seyir_digital_license_no || '-',
      'Seyir Sürücü Adı': row.seyir_driver_name || '-',
      'Eşleşen Özmal Sürücü': row.matched_driver_name || '-',
    }))), 'DijitalEhliyetIhlalleri');
    XLSX.writeFile(workbook, `seyir-driver-comparison-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const openGoogleMaps = (latitude?: number | null, longitude?: number | null) => {
    if (latitude == null || longitude == null) return;
    window.open(`https://www.google.com/maps?q=${encodeURIComponent(`${latitude},${longitude}`)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="workiom-screen">
      <div className="workiom-toolbar">
        <div className="workiom-toolbar-left">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Seyir Sürücü Karşılaştırma</h1>
            <p className="mt-1 text-gray-500">Sistem sürücüsü ve çekici plakası ile Seyir’den gelen araç/sürücü bilgisini karşılaştırın.</p>
          </div>
        </div>
        <div className="workiom-toolbar-right">
          <button type="button" onClick={handleExportExcel} className="workiom-outline-button">
            Excele Aktar
          </button>
          <button type="button" onClick={() => void loadRows()} className="workiom-outline-button">
            Yenile
          </button>
          <button type="button" onClick={handleSync} disabled={syncing} className="workiom-soft-button disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            Seyir'den Güncelle
          </button>
        </div>
      </div>

      <div className="grid gap-4 px-6 py-4 md:grid-cols-5">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Toplam Farklı Kayıt</div>
          <div className="mt-2 text-3xl font-bold text-slate-900">{summary.total}</div>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-amber-700">Plaka Farkı</div>
          <div className="mt-2 text-3xl font-bold text-amber-900">{summary.plateMismatch}</div>
        </div>
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-rose-700">Sürücü Farkı</div>
          <div className="mt-2 text-3xl font-bold text-rose-900">{summary.driverMismatch}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-700">Seyir Verisi Yok</div>
          <div className="mt-2 text-3xl font-bold text-slate-900">{summary.missingMobileData}</div>
        </div>
        <div className="rounded-xl border border-cyan-200 bg-cyan-50 p-4 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-cyan-700">İşi Yok Ama Hareketli</div>
          <div className="mt-2 text-3xl font-bold text-cyan-900">{summary.movingWithoutActiveJobs}</div>
        </div>
      </div>

      <div className="workiom-grid-wrap border border-rose-200 bg-white mb-6 max-h-[620px] overflow-auto">
        <div className="flex flex-col gap-3 border-b border-rose-100 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Dijital Ehliyet İhlal Geçmişi</h2>
            <p className="mt-1 text-sm text-slate-500">Seyir'den çekilen dijital ehliyet no ile aktif özmal sürücüler ve çekici ataması karşılaştırılır.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input type="date" value={violationStartDate} onChange={(e) => setViolationStartDate(e.target.value)} className="h-10 rounded-xl border border-slate-300 px-3 text-sm" />
            <input type="date" value={violationEndDate} onChange={(e) => setViolationEndDate(e.target.value)} className="h-10 rounded-xl border border-slate-300 px-3 text-sm" />
            <button type="button" onClick={() => void loadViolations()} className="workiom-outline-button">Filtrele</button>
            <button type="button" onClick={handleViolationScan} disabled={scanningViolations} className="workiom-soft-button disabled:opacity-50">
              <RefreshCw className={`w-4 h-4 ${scanningViolations ? 'animate-spin' : ''}`} />
              Şimdi Kontrol Et
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 border-b border-rose-100 px-4 py-3">
          {violationTabs.map((tab) => {
            const isActive = activeViolationTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveViolationTab(tab.key)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${isActive ? 'bg-rose-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
              >
                {tab.label} ({tab.count})
              </button>
            );
          })}
        </div>
        <table className="workiom-grid-table table-fixed text-sm">
          <thead>
            <tr>
              <th className="w-[150px] text-left">Son Tespit</th>
              <th className="w-[150px] text-left">İlk Tespit</th>
              <th className="w-[150px] text-left">Tip</th>
              <th className="w-[100px] text-left">Çekici</th>
              <th className="w-[100px] text-left">Dorse</th>
              <th className="w-[160px] text-left">Araç Sürücüsü</th>
              <th className="w-[140px] text-left">Araç Ehliyet No</th>
              <th className="w-[150px] text-left">Seyir Ehliyet No</th>
              <th className="w-[160px] text-left">Seyir Sürücü</th>
              <th className="w-[80px] text-left">Tekrar</th>
            </tr>
          </thead>
          <tbody>
            {filteredViolations.length === 0 ? (
              <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-500">Seçili tarihte dijital ehliyet ihlali bulunmadı.</td></tr>
            ) : filteredViolations.map((row) => (
              <tr key={`violation-${row.id}`}>
                <td>{formatDateTime(row.last_detected_at)}</td>
                <td>{formatDateTime(row.first_detected_at)}</td>
                <td><span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${row.severity === 'critical' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>{row.violation_type === 'unknown_digital_license' ? 'Bilinmeyen ehliyet' : 'Atama farkı'}</span></td>
                <td className="font-medium">{row.tractor_plate || '-'}</td>
                <td>{row.trailer_plate || '-'}</td>
                <td>{row.system_driver_name || '-'}</td>
                <td>{row.system_driver_license_no || '-'}</td>
                <td className="font-semibold text-rose-700">{row.seyir_digital_license_no || '-'}</td>
                <td>{row.seyir_driver_name || '-'}</td>
                <td>{row.check_count || 1}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="workiom-grid-wrap border border-gray-200 bg-white mb-6 max-h-[720px] overflow-auto">
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 className="text-base font-semibold text-slate-900">Workiom'da Aktif Sevkiyatı Olan Plakalar</h2>
          <div className="mt-3 max-w-md">
            <input
              type="text"
              value={activeWorkiomSearch}
              onChange={(e) => setActiveWorkiomSearch(e.target.value)}
              placeholder="Plaka, sürücü, sevkiyat no ara"
              className="h-10 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-800 outline-none focus:border-violet-500"
            />
          </div>
        </div>
        <table className="workiom-grid-table table-fixed text-sm">
          <thead>
            <tr>
              <th className="w-[110px] text-left">Sevkiyat No</th>
              <th className="w-[110px] text-left">Sipariş No</th>
              <th className="w-[110px] text-left">Taşıma Modeli</th>
              <th className="w-[120px] text-left">Nakliyeci Tipi</th>
              <th className="w-[130px] text-left">Durum</th>
              <th className="w-[130px] text-left">İşlem Durumu</th>
              <th className="w-[110px] text-left">Plaka</th>
              <th className="w-[110px] text-left">Dorse</th>
              <th className="w-[150px] text-left">Sürücü</th>
              <th className="w-[140px] text-left">Yükleme</th>
              <th className="w-[140px] text-left">Teslim</th>
              <th className="w-[150px] text-left">Konum Zamanı</th>
              <th className="w-[180px] text-left">Sürücü Konumu</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={13} className="px-4 py-10 text-center text-slate-500">Yükleniyor...</td></tr>
            ) : filteredActiveWorkiomShipments.length === 0 ? (
              <tr><td colSpan={13} className="px-4 py-10 text-center text-slate-500">Aktif Workiom sevkiyatı bulunmadı.</td></tr>
            ) : filteredActiveWorkiomShipments.map((row) => (
              <tr key={`workiom-active-${row.id}`}>
                <td className="font-medium"><div className="truncate" title={row.shipmentNo}>{row.shipmentNo}</div></td>
                <td><div className="truncate" title={row.orderCode}>{row.orderCode}</div></td>
                <td><div className="truncate" title={row.transportModel}>{row.transportModel}</div></td>
                <td><div className="truncate" title={row.carrierType}>{row.carrierType}</div></td>
                <td><div className="truncate" title={row.workiomStatusLabel}>{row.workiomStatusLabel}</div></td>
                <td><div className="truncate" title={row.processStatus}>{row.processStatus}</div></td>
                <td><div className="truncate" title={row.vehiclePlate}>{row.vehiclePlate}</div></td>
                <td><div className="truncate" title={row.trailerPlate}>{row.trailerPlate}</div></td>
                <td><div className="truncate" title={row.driverName}>{row.driverName}</div></td>
                <td><div className="max-w-[140px] truncate" title={row.pickup}>{row.pickup}</div></td>
                <td><div className="max-w-[140px] truncate" title={row.delivery}>{row.delivery}</div></td>
                <td><div className="truncate" title={formatDateTime(row.locationUpdatedAt)}>{formatDateTime(row.locationUpdatedAt)}</div></td>
                <td>
                  {row.latitude != null && row.longitude != null ? (
                    <button type="button" title={row.currentAddress} onClick={() => openGoogleMaps(row.latitude, row.longitude)} className="block max-w-[180px] truncate text-left text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-800">
                      {row.currentAddress}
                    </button>
                  ) : <div className="max-w-[180px] truncate" title={row.currentAddress}>{row.currentAddress}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="workiom-grid-wrap border border-gray-200 bg-white mb-6">
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 className="text-base font-semibold text-slate-900">Seyir Mobilden Veri Alınamayan Özmal Sürücüler</h2>
        </div>
        <table className="workiom-grid-table text-sm">
          <thead>
            <tr>
              <th className="text-left">Sürücü</th>
              <th className="text-left">Telefon</th>
              <th className="text-left">Sistem Çekici</th>
              <th className="text-left">Sistem Dorse</th>
              <th className="text-left">Son Konum Zamanı</th>
              <th className="text-left">Durum</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Yükleniyor...</td></tr>
            ) : missingRows.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Seyir verisi eksik özmal sürücü bulunmadı.</td></tr>
            ) : missingRows.map((row) => (
              <tr key={`missing-${row.driverId}`}>
                <td className="font-medium">{row.driverName || '-'}</td>
                <td>{row.phone || '-'}</td>
                <td>{row.systemVehiclePlate || '-'}</td>
                <td>{row.systemTrailerPlate || '-'}</td>
                <td>{formatDateTime(row.locationUpdatedAt)}</td>
                <td><span className="inline-flex rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800">Seyir veri yok</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="workiom-grid-wrap border border-gray-200 bg-white mb-6">
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 className="text-base font-semibold text-slate-900">Seyirde Hareket Eden Ama Aktif İşi Olmayan Özmal Sürücüler</h2>
        </div>
        <table className="workiom-grid-table text-sm">
          <thead>
            <tr>
              <th className="text-left">Sürücü</th>
              <th className="text-left">Sistem Çekici</th>
              <th className="text-left">Sistem Dorse</th>
              <th className="text-left">Hız</th>
              <th className="text-left">Son Konum Zamanı</th>
              <th className="text-left">Mevcut Konum</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Yükleniyor...</td></tr>
            ) : movingRows.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Aktif işi olmayan hareketli özmal sürücü bulunmadı.</td></tr>
            ) : movingRows.map((row) => (
              <tr key={`moving-${row.driverId}`}>
                <td className="font-medium">{row.driverName || '-'}</td>
                <td>{row.systemVehiclePlate || '-'}</td>
                <td>{row.systemTrailerPlate || '-'}</td>
                <td>{row.currentSpeed ? `${row.currentSpeed.toLocaleString('tr-TR')} km/s` : row.isVehicleRunning ? 'Hareket var' : '-'}</td>
                <td>{formatDateTime(row.locationUpdatedAt)}</td>
                <td title={row.currentAddress || '-'}>{row.currentAddress || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="workiom-grid-wrap border border-gray-200 bg-white">
        <table className="workiom-grid-table text-sm">
          <thead>
            <tr>
              <th className="text-left">Araç ID</th>
              <th className="text-left">Sistem Çekici</th>
              <th className="text-left">Seyir Çekici</th>
              <th className="text-left">Sistem Sürücü</th>
              <th className="text-left">Seyir Sürücü</th>
              <th className="text-left">Sistem Ehliyet No</th>
              <th className="text-left">Seyir Dijital Ehliyet No</th>
              <th className="text-left">Sistem Dorse</th>
              <th className="text-left">Seyir Dorse</th>
              <th className="text-left">Fark Tipi</th>
              <th className="text-left">Konum Zamanı</th>
              <th className="text-left">Mevcut Konum</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={12} className="px-4 py-10 text-center text-slate-500">Yükleniyor...</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={12} className="px-4 py-10 text-center text-slate-500">Farklı kayıt bulunmadı.</td>
              </tr>
            ) : rows.map((row) => {
              const mismatchLabels = [
                !row.plateMatches ? 'Çekici Plakası' : '',
                !row.driverMatches ? (row.licenseMatchKnown && !row.licenseMatches ? 'Dijital Ehliyet' : 'Sürücü') : '',
              ].filter(Boolean);

              return (
                <tr key={`mismatch-${row.driverId}-${row.vehicleId || 'no-vehicle'}`}>
                  <td>{row.vehicleId}</td>
                  <td className="font-medium">{row.systemVehiclePlate || '-'}</td>
                  <td>{row.seyirVehiclePlate || '-'}</td>
                  <td>{row.systemDriverName || '-'}</td>
                  <td>{row.seyirDriverName || '-'}</td>
                  <td>{row.systemDriverLicenseNo || '-'}</td>
                  <td className="font-semibold text-rose-700">{row.seyirDigitalLicenseNo || '-'}</td>
                  <td>{row.systemTrailerPlate || '-'}</td>
                  <td>{row.seyirTrailerPlate || '-'}</td>
                  <td>
                    <div className="flex flex-wrap gap-2">
                      {mismatchLabels.map((label) => (
                        <span key={label} className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700">
                          <ShieldAlert className="h-3.5 w-3.5" />
                          {label}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>{formatDateTime(row.locationUpdatedAt)}</td>
                  <td title={row.currentAddress || '-'}>{row.currentAddress || '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
